import React, { useEffect, useState, useRef, useContext } from "react";
import { Button } from "react-bootstrap";
import Swal from "sweetalert2";
import DefaultLoader from "../Loader";
import {
  DeleteAccount,
  PauseOrActiveAccount,
  SyncActions,
} from "../../../services/WaBottyServices";
import NotificationsContext from "../../../contexts/NotificationsContext";
import WhatsappConnect from "./WhatsappConnect";
import ModalComponent from "../Modal";
import styled from "styled-components";
import UserContext from "../../../contexts/UserContext";
import {
  IconBrandWhatsapp,
  IconPlayerPlay,
  IconPlayerPause,
  IconRefresh,
  IconTrash,
  IconPhone,
  IconCalendar,
  IconId,
  IconPlugConnected,
  IconWifi,
  IconWifiOff,
  IconRotate,
} from "@tabler/icons-react";

// Styled Components
const Container = styled.div`
  margin: 0;
  background: #f8f9fa;

  @media (max-width: 768px) {
    background: #ffffff;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  margin-top: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    margin-top: 10px;
    align-items: stretch;
  }
`;

const Title = styled.h2`
  color: #212529;
  font-weight: 700;
  font-size: 28px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 24px;
    justify-content: center;
  }
`;

const TitleIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #25d366, #128c7e);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
  border: 2px solid transparent;
  transition: all 0.3s ease;

  &:hover {
    border-color: #25d366;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  background: ${(props) =>
    props.color || "linear-gradient(135deg, #25d366, #128c7e)"};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    margin-bottom: 8px;
  }
`;

const StatLabel = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #212529;
  margin: 0 0 4px 0;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const StatValue = styled.div`
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
`;

const AccountCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 2px solid transparent;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${(props) => {
      if (props.status === "connected" && props.receiveChats === 1)
        return "linear-gradient(90deg, #25d366, #128c7e)";
      if (props.status === "connected" && props.receiveChats !== 1)
        return "linear-gradient(90deg, #ffc107, #fd7e14)";
      return "linear-gradient(90deg, #6c757d, #495057)";
    }};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    border-color: rgba(37, 211, 102, 0.2);
  }

  @media (max-width: 768px) {
    border-radius: 16px;
    padding: 20px;

    &:hover {
      transform: none;
    }
  }
`;

const AccountHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const AccountInfo = styled.div`
  flex: 1;
`;

const PhoneNumber = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #212529;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  background: ${(props) => {
    if (props.status === "connected" && props.receiveChats === 1)
      return "linear-gradient(135deg, #25d366, #128c7e)";
    if (props.status === "connected" && props.receiveChats !== 1)
      return "linear-gradient(135deg, #ffc107, #fd7e14)";
    if (props.status === "connecting")
      return "linear-gradient(135deg, #17a2b8, #138496)";
    return "linear-gradient(135deg, #6c757d, #495057)";
  }};
  color: white;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
`;

const AccountDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const DetailIcon = styled.div`
  width: 32px;
  height: 32px;
  background: #e9ecef;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
`;

const DetailText = styled.div`
  flex: 1;
`;

const DetailLabel = styled.div`
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
`;

const DetailValue = styled.div`
  font-size: 14px;
  color: #212529;
  font-weight: 600;
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const ActionButton = styled.button`
  background: ${(props) => {
    if (props.variant === "pause")
      return props.isActive ? "var(--primary-hover)" : "var(--primary)";
    if (props.variant === "reconnect") return "#17a2b8";
    if (props.variant === "delete") return "var(--danger-hover)";
    if (props.variant === "sync") return "var(--success)";
    return "#6c757d";
  }};
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 20px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: center;

  &:hover {
    transform: translateY(-2px);
    filter: brightness(1.1);
    box-shadow: 0 4px 12px
      ${(props) => {
        if (props.variant === "pause")
          return props.isActive
            ? "rgba(255, 193, 7, 0.3)"
            : "rgba(37, 211, 102, 0.3)";
        if (props.variant === "reconnect") return "rgba(23, 162, 184, 0.3)";
        if (props.variant === "delete") return "rgba(220, 53, 69, 0.3)";
        return "rgba(108, 117, 125, 0.3)";
      }};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 14px 20px;
    font-size: 15px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 60px 20px;
    border-radius: 16px;
  }
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  background: linear-gradient(135deg, #e9ecef, #f8f9fa);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;

  @media (max-width: 768px) {
    width: 64px;
    height: 64px;
    margin-bottom: 20px;
  }
`;

const EmptyTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const EmptyDescription = styled.p`
  font-size: 16px;
  color: #6c757d;
  margin-bottom: 32px;
  line-height: 1.5;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 24px;
  }
`;

const ConnectButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px 32px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 auto;

  &:hover {
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 18px 32px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  color: #6c757d;
`;

export default function Account() {
  const { user, bottyData, setBottyData, accountLoading } = useContext(UserContext);
  const { setNotify } = useContext(NotificationsContext);
  const [showModal, setShowModal] = useState(false);

  const connectOrReconnect = useRef({ connect: false, reconnect: false });

  const getStatusInfo = () => {
    if (!bottyData)
      return {
        status: "disconnected",
        label: "Desconectado",
        icon: IconWifiOff,
      };

    if (bottyData.status === "connected") {
      if (bottyData.receive_chats === 1) {
        return { status: "connected", label: "Conectado", icon: IconWifi };
      } else {
        return { status: "paused", label: "Pausado", icon: IconPlayerPause };
      }
    }

    if (bottyData.status === "connecting") {
      return { status: "connecting", label: "Conectando", icon: IconRefresh };
    }

    return { status: "disconnected", label: "Desconectado", icon: IconWifiOff };
  };

  const handleDeleteAccount = () => {
    Swal.fire({
      title: "Você tem certeza?",
      text: "Você está prestes a excluir a conexão do seu Bot de Whatsapp!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Excluir Conta",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Excluindo...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        DeleteAccount({
          uid: bottyData.uid,
          unique: bottyData.unique,
          b_token: bottyData.token,
        })
          .then((result) => {
            if (result) {
              setNotify({
                title: "Sucesso!",
                message: "Conexão com whatsApp excluída com sucesso!",
                type: "success",
                visible: true,
              });

              sessionStorage.removeItem("botty_account");
              setTimeout(() => {
                setBottyData(null);
                window.location.href = "/whatsapp";
              }, 2500);
            }
          })
          .catch((error) => {
            setNotify({
              title: "Oops!",
              message:
                "Ocorreu um erro ao excluir sua conexão com o Whatsapp. Por favor, tente novamente daqui alguns minutos. Se o problema persistir, contacte nosso suporte.",
              type: "error",
              visible: true,
            });
          });
      }
    });
  };

  const handlePauseOrActive = () => {
    Swal.fire({
      title: "Você tem certeza?",
      html:
        bottyData.receive_chats === 1
          ? "Você está prestes a pausar o funcionamento da IA nesta conta para todos os clientes!"
          : "Você tem certeza que deseja ativar a IA nesta conta para todos os clientes?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor:
        bottyData.receive_chats === 1 ? "#515151" : "var(--success)",
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonText:
        bottyData.receive_chats === 1 ? "Pausar o IA!" : "Ativar IA",
    }).then((result) => {
      if (result.isConfirmed) {
        PauseOrActiveAccount({
          aid: bottyData.id,
          unique: bottyData.unique,
          status: bottyData.receive_chats === 1 ? 2 : 1,
        })
          .then((result) => {
            Swal.fire({
              title: "Pausando...",
              allowOutsideClick: false,
              didOpen: () => {
                Swal.showLoading();
              },
            });
            if (result) {
              setNotify({
                title: "Sucesso!",
                message:
                  bottyData.receive_chats === 1
                    ? "Conta pausada com sucesso!"
                    : "Conta ativada com sucesso!",
                type: "success",
                visible: true,
              });

              setTimeout(() => {
                setBottyData(null);
                window.location.href = "/whatsapp";
              }, 3500);
            } else {
              setNotify({
                title: "Oops!",
                message:
                  "Ocorreu um erro ao pausar o Bot, por favor, tente novamente daqui alguns minutos.",
                type: "error",
                visible: true,
              });
            }
          })
          .catch(() => {
            setNotify({
              title: "Oops!",
              message:
                "Ocorreu um erro ao pausar o Bot, por favor, tente novamente daqui alguns minutos.",
              type: "error",
              visible: true,
            });
          });
      }
    });
  };

  const handleSync = () => {
    Swal.fire({
      title: "Sincronizar",
      html: "Esta ação irá sincronizar sua atual conta do whatsapp com o Agente de IA.",
      icon: "info",
      showConfirmButton: true,
      confirmButtonText: "Sincronizar",
      showCancelButton: true,
      cancelButtonText: "Cancelar",
      confirmButtonColor: "var(--primary)",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Sincronizando...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        let payload = {
          aid: bottyData.id,
          uid: bottyData.uid,
          ai_key_id: bottyData.ai_key_id,
        };
        SyncActions(payload)
          .then((res) => {
            console.log(res);
            setNotify({
              title: "Sucesso!",
              message: "Agente sincronizado com sucesso!",
              type: "success",
              visible: true,
            });
          })
          .catch((error) => {
            console.log(error);
            Swal.fire(
              "Oops!",
              "Ocorreu um erro ao sincronizar o agente! Por favor, tente novamente em alguns minutos, se o erro persistir, contacte nosso suporte.",
              "error"
            );
          });
      }
    });
  };

  const modalConnectProps = {
    title: !showModal.reconnect ? "Conectar Whatsapp" : "Reconectar Whatsapp",
    content: (
      <WhatsappConnect
        data={bottyData}
        connectOrReconnect={connectOrReconnect}
        setNotify={setNotify}
        setShowModal={setShowModal}
      />
    ),
    btnLabel: "Salvar",
    showModal: showModal,
    setShowModal: setShowModal,
    showFooter: false,
  };

  const statusInfo = getStatusInfo();

  if (accountLoading) {
    return (
      <Container className="main-container">
        <LoadingContainer>
          <DefaultLoader isLoading={accountLoading} />
          <p style={{ marginTop: "20px" }}>Processando...</p>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container className="main-container">
      <Header>
        <Title>
          <TitleIcon>
            <IconBrandWhatsapp size={24} />
          </TitleIcon>
          Meu WhatsApp
        </Title>
      </Header>

      {bottyData?.uid ? (
        <>
          <StatsContainer>
            <StatCard>
              <StatIcon color="linear-gradient(135deg, #25d366, #128c7e)">
                <IconBrandWhatsapp size={24} />
              </StatIcon>
              <StatLabel>WhatsApp</StatLabel>
              <StatValue>Conectado</StatValue>
            </StatCard>
            <StatCard>
              <StatIcon
                color={
                  statusInfo.status === "connected"
                    ? "linear-gradient(135deg, #28a745, #20c997)"
                    : "linear-gradient(135deg, #6c757d, #495057)"
                }
              >
                <statusInfo.icon size={24} />
              </StatIcon>
              <StatLabel>Status</StatLabel>
              <StatValue>{statusInfo.label}</StatValue>
            </StatCard>
            <StatCard>
              <StatIcon color="linear-gradient(135deg, #007bff, #0056b3)">
                <IconId size={24} />
              </StatIcon>
              <StatLabel>ID da Conta</StatLabel>
              <StatValue>#{bottyData.id}</StatValue>
            </StatCard>
          </StatsContainer>

          <AccountCard
            status={bottyData.status}
            receiveChats={bottyData.receive_chats}
          >
            <AccountHeader>
              <AccountInfo>
                <PhoneNumber>
                  <IconPhone size={24} />
                  {bottyData.phone}
                </PhoneNumber>
                <StatusBadge
                  status={bottyData.status}
                  receiveChats={bottyData.receive_chats}
                >
                  <statusInfo.icon size={14} />
                  {statusInfo.label}
                </StatusBadge>
              </AccountInfo>
            </AccountHeader>

            <AccountDetails>
              <DetailItem>
                <DetailIcon>
                  <IconId size={16} />
                </DetailIcon>
                <DetailText>
                  <DetailLabel>ID da Conta</DetailLabel>
                  <DetailValue>#{bottyData.id}</DetailValue>
                </DetailText>
              </DetailItem>

              <DetailItem>
                <DetailIcon>
                  <IconCalendar size={16} />
                </DetailIcon>
                <DetailText>
                  <DetailLabel>Data de Criação</DetailLabel>
                  <DetailValue>{bottyData.createdAt}</DetailValue>
                </DetailText>
              </DetailItem>
            </AccountDetails>

            <ActionsContainer>
              <ActionButton
                variant="pause"
                isActive={bottyData.receive_chats === 1}
                onClick={handlePauseOrActive}
              >
                {bottyData.receive_chats === 1 ? (
                  <>
                    <IconPlayerPause size={18} />
                    Pausar IA
                  </>
                ) : (
                  <>
                    <IconPlayerPlay size={18} />
                    Ativar IA
                  </>
                )}
              </ActionButton>
              <ActionButton variant="sync" onClick={handleSync}>
                <IconRotate size={18} />
                Sincronizar
              </ActionButton>

              <ActionButton
                variant="reconnect"
                onClick={() => {
                  connectOrReconnect.current = "reconnect";
                  setShowModal(true);
                }}
              >
                <IconRefresh size={18} />
                Reconectar
              </ActionButton>

              <ActionButton variant="delete" onClick={handleDeleteAccount}>
                <IconTrash size={18} />
                Excluir
              </ActionButton>
            </ActionsContainer>
          </AccountCard>
        </>
      ) : (
        <EmptyState>
          <EmptyIcon>
            <IconBrandWhatsapp size={40} />
          </EmptyIcon>
          <EmptyTitle>Nenhuma conta conectada</EmptyTitle>
          <EmptyDescription>
            Conecte sua conta do WhatsApp para começar a usar o agente e
            automatizar suas conversas com os clientes.
          </EmptyDescription>
          <ConnectButton
            onClick={() => {
              connectOrReconnect.current = "connect";
              setShowModal(true);
            }}
          >
            <IconPlugConnected size={20} />
            Conectar Conta
          </ConnectButton>
        </EmptyState>
      )}

      {showModal && <ModalComponent props={modalConnectProps} />}
    </Container>
  );
}
